/**
 * OptionCarousel — Horizontal scrollable option cards
 *
 * Evolved from OptionCard.tsx — reuses the renderDetailWithLinks pattern.
 * Each card: title, details, image, CTA button, URL.
 */
import React from 'react';
import type { OptionCarouselData, BookingCardData } from '../types';
import { ChevronRight } from '../../shared';

/**
 * Render detail text with clickable links (markdown + raw URLs).
 * Reused from OptionCard.tsx.
 */
function renderDetailWithLinks(text: string): React.ReactNode {
  const linkPattern = /\[([^\]]+)\]\((https?:\/\/[^)]+)\)|(https?:\/\/[^\s,)<]+)/g;
  const parts: React.ReactNode[] = [];
  let lastIdx = 0;
  let match;

  while ((match = linkPattern.exec(text)) !== null) {
    if (match.index > lastIdx) {
      parts.push(text.substring(lastIdx, match.index));
    }
    const linkText =
      match[1] ||
      (() => {
        try {
          return new URL(match[3]).hostname.replace('www.', '');
        } catch {
          return match[3];
        }
      })();
    const linkUrl = match[2] || match[3];
    parts.push(
      <a
        key={match.index}
        href={linkUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="text-cyan-400 hover:text-cyan-300 underline underline-offset-2 decoration-cyan-400/40"
      >
        {linkText}
      </a>,
    );
    lastIdx = match.index + match[0].length;
  }

  if (parts.length === 0) return text;
  if (lastIdx < text.length) parts.push(text.substring(lastIdx));
  return <>{parts}</>;
}

interface OptionCarouselProps {
  data: OptionCarouselData;
  onAction?: (prompt: string) => void;
  onBookAction?: (bookingData: BookingCardData) => void;
}

export const OptionCarousel: React.FC<OptionCarouselProps> = ({ data, onAction, onBookAction }) => {
  const { options } = data;

  if (!options || options.length === 0) return null;

  return (
    <div className="relative">
      <div className="flex gap-4 overflow-x-auto pb-3 snap-x snap-mandatory scrollbar-thin scrollbar-thumb-cyan-400/20 scrollbar-track-transparent">
        {options.map((option, index) => (
          <div
            key={option.id || index}
            className="flex-shrink-0 w-72 snap-center flex flex-col rounded-2xl bg-black/50 backdrop-blur-xl border border-cyan-400/25 hover:border-cyan-400/50 transition-all duration-300 hover:shadow-[0_0_25px_rgba(34,211,238,0.2)] overflow-hidden"
          >
            {/* Option number badge */}
            <div className="absolute top-3 left-3 z-10 w-7 h-7 rounded-full bg-cyan-400/10 border border-cyan-400/40 flex items-center justify-center shadow-[0_0_10px_rgba(34,211,238,0.2)]">
              <span className="text-cyan-300 font-bold text-xs">{index + 1}</span>
            </div>

            {/* Image */}
            {option.image && (
              <div className="relative w-full h-36 bg-gradient-to-br from-cyan-900/20 to-black/50">
                <img
                  src={option.image}
                  alt={option.title}
                  className="w-full h-full object-cover opacity-80"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>
            )}

            {/* Content */}
            <div className="p-4 flex flex-col flex-grow">
              <h4 className="text-base font-bold text-cyan-400 mb-1 drop-shadow-[0_0_3px_rgba(34,211,238,0.3)]">
                {option.title}
              </h4>

              {option.subtitle && (
                <p className="text-cyan-400/60 text-xs mb-2 font-medium">{option.subtitle}</p>
              )}

              {/* Details */}
              <ul className="space-y-1.5 mb-3 flex-grow">
                {option.details.slice(0, 4).map((detail, i) => (
                  <li key={i} className="flex items-start text-xs text-cyan-300/80 leading-relaxed">
                    <span className="text-cyan-400 mr-1.5 drop-shadow-[0_0_3px_rgba(34,211,238,0.5)]">
                      ›
                    </span>
                    <span>{renderDetailWithLinks(detail)}</span>
                  </li>
                ))}
              </ul>

              {/* Highlights */}
              {option.highlights && option.highlights.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {option.highlights.map((h, i) => (
                    <span
                      key={i}
                      className="px-2 py-0.5 text-[10px] rounded-xl bg-cyan-400/10 text-cyan-300 border border-cyan-400/30 font-medium"
                    >
                      {h}
                    </span>
                  ))}
                </div>
              )}

              {/* Metadata */}
              {option.metadata && Object.keys(option.metadata).length > 0 && (
                <div className="grid grid-cols-2 gap-1.5 mb-3 text-[10px]">
                  {Object.entries(option.metadata).map(([key, value]) => (
                    <div key={key} className="flex flex-col">
                      <span className="text-cyan-400/50 uppercase tracking-wider font-bold">
                        {key}
                      </span>
                      <span className="text-cyan-200 font-medium">{value}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* CTA Button */}
              <div className="mt-auto pt-3">
                <button
                  onClick={() => {
                    const intent = option.actionIntent || option.action_intent;
                    if (intent === 'INITIATE_CHECKOUT') {
                      const payload = option.payload || {};
                      const bookingData: BookingCardData = {
                        provider: String(payload.provider || payload.merchantName || option.metadata?.provider || option.subtitle || 'Booking'),
                        title: option.title,
                        price: String(payload.amount || payload.price || option.metadata?.price || ''),
                        currency: String(payload.currency || option.metadata?.currency || ''),
                        details: option.details.map((detail, i) => ({ label: `Detail ${i + 1}`, value: detail })),
                        image: option.image,
                        bookingUrl: String(payload.checkout_url || option.url || ''),
                        ctaLabel: option.ctaLabel,
                      };

                      if (onBookAction) {
                        onBookAction(bookingData);
                      } else if (bookingData.bookingUrl) {
                        window.open(bookingData.bookingUrl, '_blank', 'noopener,noreferrer');
                      } else if (onAction) {
                        onAction(`Proceed with checkout for ${option.title}`);
                      }
                    } else if (intent === 'OAUTH_PROMPT') {
                      // Trigger OAuth connection flow
                      if (onAction) {
                        onAction(`Connect ${option.payload?.provider || option.title} to get live data`);
                      }
                    } else if (option.url) {
                      window.open(option.url, '_blank', 'noopener,noreferrer');
                    } else if (onAction) {
                      onAction(`Tell me more about ${option.title}`);
                    }
                  }}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-cyan-400/15 hover:bg-cyan-400/25 border border-cyan-400/40 hover:border-cyan-400/60 text-cyan-200 hover:text-cyan-100 text-xs font-bold tracking-wide transition-all duration-300 shadow-[0_0_10px_rgba(34,211,238,0.15)] hover:shadow-[0_0_20px_rgba(34,211,238,0.35)] active:scale-95"
                >
                  {option.ctaLabel || (option.url ? 'Open' : 'Learn More')}
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default OptionCarousel;
